/**
 * Live validation — a port of the exp editor's `runValidation`, widened to the
 * per-asset model (spec 2026-08-22-02 §11). Issues are data, never prose: the
 * React panel translates `planEditor.issues.<code>` with `params`, so every
 * message exists in EN and FR.
 */
import { dist, projectToLine } from './geometry'
import { mmToUnits } from './calibrate'
import { DEFAULT_WALL_THICKNESS } from './openings'
import { duplicateFootprints, elementByKey, groupByRepresentation, holeFits, networkOf, ownerName, ownerTypeCode, sceneMmPerUnit, spaceHoles, type SceneDoc } from './scene'

/** `room` and its sub-types — the `room_` prefix is the ontology's own contract for the family. */
const isRoomType = (code: string | undefined): boolean => code === 'room' || Boolean(code?.startsWith('room_'))

export type IssueSeverity = 'error' | 'warning' | 'info'

export interface ValidationIssue {
    code: string
    severity: IssueSeverity
    /** The element the issue points at — clicking the row pans to it. */
    elementKey?: string
    params?: Record<string, string | number>
}

/**
 * Walls shorter than this are almost always a mis-click — **in millimetres**.
 *
 * That unit is the whole subtlety, and it was wrong until spec 2026-09-05-07
 * §0: the editor works in the frame's own *plan units*, so on a frame whose
 * `mm_per_unit` is 14 000 a perfectly ordinary 0.03-unit wall is 420 mm, and
 * comparing 0.03 against 50 reported "Wall shorter than 50 mm (0 mm)" on
 * every wall of every plan. Thirty bogus errors is not a validation, it is
 * noise that trains people to ignore the panel.
 *
 * So the threshold is converted through the frame's scale, and an uncalibrated
 * frame — where there is no scale, and 0.03 units is genuinely not a length —
 * is simply not length-checked.
 */
export const MIN_WALL_LENGTH = 50

export function validate(doc: SceneDoc): ValidationIssue[] {
    const issues: ValidationIssue[] = []
    const mmPerUnit = sceneMmPerUnit(doc)
    // Plan units per millimetre; `null` disables every length check below.
    const minWallLength = mmPerUnit && mmPerUnit > 0 ? MIN_WALL_LENGTH / mmPerUnit : null
    const mmOf = (units: number) => (mmPerUnit && mmPerUnit > 0 ? units * mmPerUnit : units)

    // Duplicate local ids inside one representation would collide on save.
    const seen = new Set<string>()
    for (const el of doc.elements) {
        const id = `${el.source.repUid ?? el.owner.assetUid ?? el.owner.newAsset?.key ?? 'new'}::${el.source.localId}`
        if (seen.has(id)) {
            issues.push({ code: 'duplicate_id', severity: 'error', elementKey: el.key, params: { id: el.source.localId } })
        }
        seen.add(id)
    }

    for (const el of doc.elements) {
        if (el.kind === 'wall') {
            const len = dist(el.start, el.end)
            if (minWallLength !== null && len < minWallLength) {
                issues.push({
                    code: 'wall_too_short', severity: 'error', elementKey: el.key,
                    params: { length: Math.round(mmOf(len)) },
                })
            }
            continue
        }

        if (el.kind === 'space') {
            if (el.boundary.length < 3) {
                issues.push({ code: 'ring_too_small', severity: 'error', elementKey: el.key, params: { count: el.boundary.length } })
                continue
            }
            // A hole dragged across its room's edge or onto another hole would
            // take off floor that is not there — and the server refuses the
            // save (spec 2026-10-19-07), so it is said here first.
            spaceHoles(el).forEach((hole, i) => {
                if (!holeFits(el, hole, i)) {
                    issues.push({ code: 'space_hole_invalid', severity: 'error', elementKey: el.key, params: { index: i + 1 } })
                }
            })
            // A space is nameless only when neither the drawing nor the asset
            // it depicts has a name — and never when that asset is a room: a
            // room sent without a name is named by the server from its type
            // and number (spec 2026-08-31-02 §2), which is the point of not
            // typing one.
            if (!el.name.trim() && !ownerName(el.owner) && !isRoomType(ownerTypeCode(el.owner))) {
                issues.push({ code: 'room_missing_name', severity: 'warning', elementKey: el.key })
            }
            continue
        }

        if (el.kind === 'opening') {
            const host = el.wallKey ? elementByKey(doc, el.wallKey) : undefined
            if (!host) {
                if (el.wallRef) {
                    issues.push({ code: 'opening_dangling_ref', severity: 'warning', elementKey: el.key, params: { wall: el.wallRef.wallId } })
                } else {
                    issues.push({ code: 'opening_no_wall', severity: 'warning', elementKey: el.key })
                }
            } else if (host.kind === 'wall') {
                const lp = projectToLine(el.center, host.start, host.end)
                // Validation is pure geometry with no zoom: an uncalibrated frame
                // reads units as millimetres here, which only affects a host wall
                // stored without a thickness.
                const thickness = host.thickness || el.wallThickness || mmToUnits(DEFAULT_WALL_THICKNESS, doc.calibration)
                // Same unit trap as the wall length: the 50 mm slack and the
                // 300 mm overshoot are millimetres, the geometry is plan units.
                const slack = minWallLength !== null ? minWallLength : 50
                if (lp.d > thickness / 2 + slack) {
                    issues.push({
                        code: 'opening_off_line', severity: 'error', elementKey: el.key,
                        params: { distance: Math.round(mmOf(lp.d)) },
                    })
                }
                const wallLen = dist(host.start, host.end)
                const beyond = Math.max(-lp.t, lp.t - 1) * wallLen
                const overshoot = minWallLength !== null ? minWallLength * 6 : 300
                if (beyond > Math.max(el.width, overshoot)) {
                    issues.push({
                        code: 'opening_past_end', severity: 'error', elementKey: el.key,
                        params: { distance: Math.round(mmOf(beyond)) },
                    })
                }
            }
        }

        if (el.kind === 'equipment' && !el.typeCode) {
            issues.push({ code: 'equipment_no_type', severity: 'warning', elementKey: el.key })
        }

        // A network the organization's catalogue does not list still saves
        // and draws (neutral grey, under its raw code) — but nobody can tell
        // what it is, so it is named.
        if ((el.kind === 'run' || el.kind === 'riser') && !networkOf(doc, el.network)) {
            issues.push({ code: 'run_unknown_network', severity: 'warning', elementKey: el.key, params: { network: el.network } })
        }
    }

    // Asset linkage: geometry with neither an existing asset nor a pending one
    // is informational (it still saves — it just depicts nothing in the
    // inventory); a parent that fell back to the frame asset is a warning.
    for (const el of doc.elements) {
        if (!el.owner.assetUid && !el.owner.newAsset) {
            issues.push({ code: 'no_asset_link', severity: 'info', elementKey: el.key, params: { kind: el.kind } })
        }
        if (el.owner.newAsset?.parentRefused) {
            // An error, not a warning: this one cannot be written at all, and
            // saving it anyway loses the drawing to a 422 nobody sees.
            issues.push({
                code: 'parent_ontology_refused',
                severity: 'error',
                elementKey: el.key,
                params: { name: el.owner.newAsset.name, type: el.owner.newAsset.typeCode },
            })
        } else if (el.owner.newAsset?.parentFallback) {
            issues.push({
                code: 'parent_fallback',
                severity: 'warning',
                elementKey: el.key,
                params: { name: el.owner.newAsset.name, type: el.owner.newAsset.typeCode },
            })
        }
    }

    // §4 — split warning: a saved representation carrying more than one
    // non-wall element that no element asset owns. Bundles accumulate anonymous
    // spaces and equipments silently; naming the problem is what turns them
    // into inventory. Wall bundles are exempt: a floor's structure legitimately
    // lives in one representation — and so do its network runs, which depict a
    // network asset by link rather than owning one each.
    for (const group of groupByRepresentation(doc)) {
        if (!group.repUid) continue
        // "Not owned by an element asset": either nothing owns it, or the
        // representation's own asset does — the all-in-one leftover.
        const unsplit = group.elements.filter((el) =>
            el.kind !== 'wall'
            && el.kind !== 'run'
            && el.kind !== 'riser'
            && !el.owner.newAsset
            && (!el.owner.assetUid || el.owner.assetUid === group.owner.assetUid))
        if (unsplit.length > 1) {
            issues.push({
                code: 'split_needed',
                severity: 'warning',
                elementKey: unsplit[0].key,
                params: {
                    count: unsplit.length,
                    layer: group.owner.assetName || group.owner.assetUid || '—',
                },
            })
        }
    }

    // §4 — duplicate-layer warning: two representations of the same asset
    // resolved into the same frame. No DB constraint forbids it (spec
    // 2026-08-25-03 §5) because one representation's value can legitimately
    // hold several elements — so it is a warning about a *drawing*, not an
    // error about data.
    for (const dup of duplicateFootprints(doc)) {
        const el = doc.elements.find((e) => e.owner.assetUid === dup.assetUid)
        issues.push({
            code: 'duplicate_layer',
            severity: 'warning',
            elementKey: el?.key,
            params: {
                name: el?.owner.assetName || dup.assetUid,
                count: dup.footprints.length,
            },
        })
    }

    return issues
}

export const errorCount = (issues: ValidationIssue[]): number =>
    issues.filter((i) => i.severity === 'error').length
